import { Box, BoxProps, Flex, Input, Text } from '@chakra-ui/react';
import debounce from 'lodash.debounce';
import { useRouter } from 'next/router';
import { ReactNode, useEffect, useRef, useState } from 'react';

import { updateQueryParam } from '@/utils/helpers/params';
import useParams from '@/utils/hooks/useParams';

const PackageTabContent: React.FC<
  BoxProps & {
    description: string;
    placeholder: string;
    headers: Array<{ key: string; width: string; children: string }>;
    rows: ReactNode;
    prepend: string;
  }
> = ({ description, placeholder, headers, rows, prepend, ...props }) => {
  const router = useRouter();
  const params = useParams(router);
  const query = decodeURIComponent(params.get('query') || '');

  const [value, setValue] = useState(decodeURIComponent(query));

  useEffect(() => {
    if (query) {
      return;
    }

    setValue('');
  }, [query]);

  const handleSearchAfter300ms = useRef(
    debounce((query: string) => {
      const url = `${prepend}?${updateQueryParam(query, params).toString()}`;
      router.push(url, undefined, {
        scroll: false,
        shallow: true,
      });
    }, 300),
  );

  return (
    <Box {...props}>
      <Box mb="30px">
        <Text display="inline" color="grey.0" lineHeight="25px">
          {description}
        </Text>
        <Input
          mt="30px"
          p="10px 15px"
          fontSize="14px"
          lineHeight="20px"
          focusBorderColor="purple.0"
          placeholder={placeholder}
          value={value}
          onChange={e => {
            setValue(e.currentTarget.value);
            handleSearchAfter300ms.current(e.currentTarget.value);
          }}
        />
      </Box>
      <Box borderBottom="1px solid" borderBottomColor="white.4">
        <Flex display={{ base: 'none', xl: 'flex' }}>
          {headers.map(({ key, width, children }) => (
            <Box key={key} width={width} p="15px" mr="auto" _last={{ mr: '0px' }}>
              <Text as="span" fontSize="14px" lineHeight="30px" color="grey.0">
                {children}
              </Text>
            </Box>
          ))}
        </Flex>
      </Box>
      <Box>{rows}</Box>
    </Box>
  );
};

export default PackageTabContent;
